import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import { addDays, subDays } from 'date-fns';

import { useQueryKpiSettings } from '@hooks/kpis/useQueryKpiSettings';

import { map } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { IDateInterval } from '@interfaces/kpi/kpiCommon';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { EVendorsValues } from '@enums/common';
import { EFormFields } from '@enums/kpi-api';

import { HALF_YEAR_DATE_INTERVAL, YEAR_DATE_INTERVAL } from '@constants/KPI/kpi-common';
import { routes } from '@constants/routes';

export const useParametersLogic = ({
	isMap,
	setIsWithInterval,
	formMethods,
	defaultFrom,
}: {
	isMap?: boolean;
	setIsWithInterval?: Dispatch<SetStateAction<boolean>>;
	formMethods: UseFormReturn;
	defaultFrom: number;
}) => {
	const [isOranVendor, setIsOranVendor] = useState(false);
	const [isMapMode, setIsMapMode] = useState(false);
	const [isOpenParams, setIsOpenParams] = useState(true);

	const { activeSiteMarkers, activeCustomPolygons } = useAppSelector(map);

	const { pathname } = useLocation();

	const vendor = formMethods.watch(EFormFields.vendor);
	const from = formMethods.watch(EFormFields.from);
	const to = formMethods.watch(EFormFields.to);
	const kpiValues = formMethods.watch(EFormFields.kpi);
	const dateSelectionMode: string | undefined = formMethods.watch(EFormFields.dateSelectionMode);
	const dynamicDateRange: TSelectOptionType<string>= formMethods.watch(EFormFields.dynamicDateRange);
	const startDateOffset: TSelectOptionType<number> = formMethods.watch(EFormFields.startDateOffset);

	const {
		isAutomaticGranularity,
		granularity,
		initTreeCountersData,
		treeKpiIds,
		treeCountersIds,
		initTreeKpiData,
	} = formMethods.watch();

	const { maxSelection } = useQueryKpiSettings(vendor?.value);

	useEffect(() => {
		if (setIsWithInterval) {
			const isOranVendor = vendor?.value === EVendorsValues.oran;

			setIsWithInterval(isOranVendor);
			setIsOranVendor(isOranVendor);
		}
	}, [vendor?.value, setIsWithInterval]);

	useEffect(() => {
		setIsMapMode(pathname === routes.map.index);
	}, [pathname]);

	const containerClasses = useMemo(() => cn('f5gParametersContainer', {
		'f5gParametersContainerClose': !isOpenParams,
		'f5gParametersContainerOnMap': isMap,
	}), [isOpenParams, isMap]);

	const containerBody = useMemo(() => cn('f5gParametersBody', {
		'f5gParametersBodyClose': !isOpenParams,
	}), [isOpenParams]);

	const maxYearInterval: number = isOranVendor ? YEAR_DATE_INTERVAL : HALF_YEAR_DATE_INTERVAL;
	const minDate = useMemo(() => subDays(defaultFrom, maxYearInterval), [defaultFrom, maxYearInterval]);

	const includeDateIntervalsFrom: IDateInterval[] | undefined = useMemo(() => to
			? [{
				start: subDays(to, maxYearInterval),
				end: to,
			}]
			: undefined,
		[to, maxYearInterval],
	);

	const includeDateIntervalsTo: IDateInterval[] | undefined = useMemo(() => {
		return from
			? [{
				start: from,
				end: addDays(from, maxYearInterval),
			}]
			: undefined;
	}, [from, maxYearInterval]);

	const toggleParams = useCallback(() => {
		setIsOpenParams(prev => !prev);
	}, []);

	return {
		vendor,
		from,
		to,
		isOranVendor,
		isMapMode,
		isOpenParams,
		toggleParams,
		containerClasses,
		containerBody,
		activeSiteMarkers,
		activeCustomPolygons,
		maxSelection,
		maxYearInterval,
		minDate,
		isAutomaticGranularity,
		granularity,
		initTreeCountersData,
		treeKpiIds,
		treeCountersIds,
		initTreeKpiData,
		kpiValues,
		includeDateIntervalsFrom,
		includeDateIntervalsTo,
		dateSelectionMode,
		dynamicDateRange,
		startDateOffset,
	};
};
